import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, CircularProgress, Alert, Chip, Fade, Stack,
  Fab, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Grid, IconButton, Tooltip, Divider,
} from '@mui/material';
import {
  EmojiEvents, PlayCircle, Schedule, CheckCircle, Add, Close,
} from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';
import EventsTable from '../components/EventsTable';

// ─── Shared Design Tokens ─────────────────────────────────────────────────────
const BG        = '#f4f7f5';
const SURFACE   = '#ffffff';
const BORDER    = '#d6e4de';
const ACCENT    = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';

// ─── Shared input style ───────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSelect-icon': { color: TEXT_SEC },
};

const now = new Date();

const categorize = (events) => ({
  ongoing: events.filter((e) => new Date(e.eventStartDate) <= now && new Date(e.eventFinishDate) >= now),
  future:  events.filter((e) => new Date(e.eventStartDate) > now),
  past:    events.filter((e) => new Date(e.eventFinishDate) < now),
});

const getUserRole = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) return JSON.parse(user).role;
  } catch { /* ignore */ }
  return null;
};

const getEntityId = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) return JSON.parse(user).entity;
  } catch { /* ignore */ }
  return '';
};

const defaultForm = {
  name: '', type: 'tournament', format: 'elimination', entryType: 'free',
  eventStartDate: '', eventFinishDate: '', applicationDeadline: '',
  status: 'open', locationName: '', locationCity: '', locationCountry: '',
  contactName: '', contactEmail: '', contactPhone: '',
};

const sectionLabel = (text) => (
  <Grid item xs={12}>
    <Typography variant="caption" sx={{
      color: TEXT_SEC, fontWeight: 700, fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 0.5,
    }}>
      {text}
    </Typography>
  </Grid>
);

const Event = () => {
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [tab, setTab]             = useState(0);
  const [open, setOpen]           = useState(false);
  const [form, setForm]           = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const userRole = getUserRole();
  const showFab  = userRole !== 'user';

  const fetchEvents = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await apiCall((headers) => axios.get(`${BASE_URL}/events/participating`, { headers }));
      setEvents(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to fetch events');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleOpen  = () => { setForm(defaultForm); setSubmitError(''); setOpen(true); };
  const handleClose = () => { if (!submitting) setOpen(false); };
  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const entityId = getEntityId();
      const payload = {
        name: form.name, type: form.type, format: form.format,
        entryType: form.entryType, status: form.status, entityId,
        eventStartDate:      form.eventStartDate      ? new Date(form.eventStartDate).toISOString()      : undefined,
        eventFinishDate:     form.eventFinishDate     ? new Date(form.eventFinishDate).toISOString()     : undefined,
        applicationDeadline: form.applicationDeadline ? new Date(form.applicationDeadline).toISOString() : undefined,
        location:    [{ name: form.locationName, city: form.locationCity, country: form.locationCountry }],
        contactInfo: { name: form.contactName, email: form.contactEmail, phone: form.contactPhone },
      };
      await apiCall((headers) => axios.post(`${BASE_URL}/events`, payload, { headers }));
      setOpen(false);
      fetchEvents(true);
    } catch (e) {
      setSubmitError(e.response?.data?.message || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const { ongoing, future, past } = categorize(events);
  const tabData = [
    { label: 'En cours',  icon: <PlayCircle />,  data: ongoing, color: 'error'   },
    { label: 'À venir',   icon: <Schedule />,    data: future,  color: 'info'    },
    { label: 'Terminés',  icon: <CheckCircle />, data: past,    color: 'default' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', bgcolor: BG, minHeight: '100vh' }}>
      <Fade in timeout={400}>
        <Box>
          {/* ── Header ── */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{ p: 0.75, borderRadius: '10px', bgcolor: ACCENT_BG, display: 'flex' }}>
              <EmojiEvents sx={{ fontSize: 28, color: ACCENT }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: TEXT_PRI }}>
                Événements
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_SEC }}>
                Gérer les tournois, ligues et événements amicaux
              </Typography>
            </Box>
            <Chip
              label={`${events.length} au total`}
              size="small"
              sx={{ ml: 'auto', bgcolor: ACCENT_BG, color: ACCENT, border: `1px solid ${BORDER}`, fontWeight: 600 }}
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>
          )}

          {/* ── Tabs ── */}
          <Box sx={{ borderBottom: `1px solid ${BORDER}`, mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { color: TEXT_SEC, textTransform: 'none', fontWeight: 600, minHeight: 44 },
                '& .MuiTab-root.Mui-selected': { color: ACCENT },
                '& .MuiTabs-indicator': { bgcolor: ACCENT },
              }}
            >
              {tabData.map(({ label, icon, data, color }) => (
                <Tab
                  key={label}
                  icon={icon}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {label}
                      <Chip
                        label={data.length}
                        size="small"
                        color={color}
                        sx={{ height: 18, fontSize: 11, fontWeight: 700 }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {/* ── Content ── */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : (
            <EventsTable events={tabData[tab].data} onRefresh={() => fetchEvents(true)} />
          )}
        </Box>
      </Fade>

      {/* ── FAB ── */}
      {showFab && (
        <Tooltip title="Créer un nouvel événement" placement="left">
          <Fab
            onClick={handleOpen}
            sx={{
              position: 'fixed', bottom: 32, right: 32, zIndex: 1200,
              bgcolor: ACCENT, color: '#fff',
              boxShadow: '0 4px 20px rgba(30,107,69,0.40)',
              '&:hover': { bgcolor: '#175c39', boxShadow: '0 6px 24px rgba(30,107,69,0.50)' },
            }}
          >
            <Add />
          </Fab>
        </Tooltip>
      )}

      {/* ── Create Event Dialog ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            bgcolor: SURFACE,
            boxShadow: '0 8px 32px rgba(30,107,69,0.10)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ p: 0.5, borderRadius: '8px', bgcolor: ACCENT_BG, display: 'flex' }}>
              <EmojiEvents sx={{ fontSize: 20, color: ACCENT }} />
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: TEXT_PRI }}>
              Créer un nouvel événement
            </Typography>
          </Stack>
          <IconButton onClick={handleClose} disabled={submitting} size="small"
            sx={{ color: TEXT_SEC, '&:hover': { bgcolor: ACCENT_BG } }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ borderColor: BORDER }} />

        <DialogContent sx={{ bgcolor: BG, pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{submitError}</Alert>
          )}

          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                label="Nom de l'événement" value={form.name}
                onChange={handleChange('name')} fullWidth required size="small" sx={inputSx}
              />
            </Grid>

            {/* Type & Format */}
            <Grid item xs={6}>
              <TextField select label="Type" value={form.type}
                onChange={handleChange('type')} fullWidth size="small" sx={inputSx}>
                {['tournament', 'single_game', 'training', 'trial'].map((v) => (
                  <MenuItem key={v} value={v} sx={{ color: TEXT_PRI }}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Format" value={form.format}
                onChange={handleChange('format')} fullWidth size="small" sx={inputSx}>
                {['elimination', 'round_robin', 'playoff', 'group_stage'].map((v) => (
                  <MenuItem key={v} value={v} sx={{ color: TEXT_PRI }}>{v}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Entry & Status */}
            <Grid item xs={6}>
              <TextField select label="Type d'inscription" value={form.entryType}
                onChange={handleChange('entryType')} fullWidth size="small" sx={inputSx}>
                {['free', 'paid'].map((v) => (
                  <MenuItem key={v} value={v} sx={{ color: TEXT_PRI }}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Statut" value={form.status}
                onChange={handleChange('status')} fullWidth size="small" sx={inputSx}>
                {['open', 'closed', 'draft', 'cancelled'].map((v) => (
                  <MenuItem key={v} value={v} sx={{ color: TEXT_PRI }}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Dates */}
            <Grid item xs={6}>
              <TextField label="Date de début" type="datetime-local" value={form.eventStartDate}
                onChange={handleChange('eventStartDate')} fullWidth size="small"
                InputLabelProps={{ shrink: true }} sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Date de fin" type="datetime-local" value={form.eventFinishDate}
                onChange={handleChange('eventFinishDate')} fullWidth size="small"
                InputLabelProps={{ shrink: true }} sx={inputSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Date limite d'inscription" type="datetime-local"
                value={form.applicationDeadline} onChange={handleChange('applicationDeadline')}
                fullWidth size="small" InputLabelProps={{ shrink: true }} sx={inputSx} />
            </Grid>

            {/* Location */}
            {sectionLabel('Lieu')}
            <Grid item xs={12}>
              <TextField label="Nom du lieu" value={form.locationName}
                onChange={handleChange('locationName')} fullWidth size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Ville" value={form.locationCity}
                onChange={handleChange('locationCity')} fullWidth size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Pays" value={form.locationCountry}
                onChange={handleChange('locationCountry')} fullWidth size="small" sx={inputSx} />
            </Grid>

            {/* Contact */}
            {sectionLabel('Informations de contact')}
            <Grid item xs={12}>
              <TextField label="Nom du contact" value={form.contactName}
                onChange={handleChange('contactName')} fullWidth size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="E-mail" type="email" value={form.contactEmail}
                onChange={handleChange('contactEmail')} fullWidth size="small" sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Téléphone" value={form.contactPhone}
                onChange={handleChange('contactPhone')} fullWidth size="small" sx={inputSx} />
            </Grid>
          </Grid>
        </DialogContent>

        <Divider sx={{ borderColor: BORDER }} />
        <DialogActions sx={{ px: 3, py: 2, gap: 1, bgcolor: SURFACE }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !form.name}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Add />}
            sx={{
              bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', fontWeight: 600,
              boxShadow: '0 4px 14px rgba(30,107,69,0.25)',
              '&:hover': { bgcolor: '#175c39', boxShadow: '0 6px 18px rgba(30,107,69,0.35)' },
              '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
            }}
          >
            {submitting ? 'Création…' : "Créer l'événement"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Event;